import React, { Component } from 'react';
import Subheader from 'material-ui/Subheader';
import { List, ListItem } from 'material-ui/List';
import { RaisedButton } from 'material-ui';
import { Formik, Field } from 'formik';
import * as yup from 'yup';

import { Theme } from '../../theme/theme';
import { Loading } from '../../common/components/loading.component';
import { FontAwesome } from '../../common/components/font-awesome';
import { CreateStudentVariables } from '../../ApolloComponents';
import { FormikMaterialTextField } from '../../common/components/material-text-field.formik';

const styles = {
  header: {
    lineHeight: 'initial',
    paddingTop: 10
  },
  miniListItem: {
    paddingTop: 0,
    paddingBottom: 0
  },
  identifierField: {
    width: 230,
    marginLeft: 15
  },
  lecturerField: {
    width: 100,
    marginRight: 10
  },
  avatar: {
    marginTop: 12
  },
  headerNote: {
    color: Theme.ACTIVE2_COLOR
  }
};

interface Props {
  initialStudent?: Partial<CreateStudentVariables['student']>;
  createEditStudent: (vars: CreateStudentVariables) => any;
  onSuccess?: () => void;
}

const defaultValues: CreateStudentVariables['student'] = {
  username: '',
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: ''
};

const createStudentSchema = yup.object().shape({
  username: yup.string().trim().required('Username is required'),
  firstName: yup.string().trim().required('First name is required'),
  lastName: yup.string().trim().required('Last name is required'),
  email: yup.string().trim().email('Enter a valid email').required('Email is required'),
  phoneNumber: yup
    .string()
    .trim()
    .matches(/^\+?[0-9]{10,15}$/, 'Enter a valid phone number with country code')
    .required('Phone number is required')
});

const formatStudentInput = (
  student: CreateStudentVariables['student']
): CreateStudentVariables['student'] => ({
  ...student,
  username: student.username.trim().toLowerCase(),
  firstName: student.firstName.trim(),
  lastName: student.lastName.trim(),
  email: student.email.trim().toLowerCase(),
  phoneNumber: student.phoneNumber.trim().replace(/[\s()-]/g, '')
});

export class CreateEditStudentFormView extends Component<
  Props,
  { error: string; isDone: boolean }
> {
  state = {
    error: '',
    isDone: false
  };

  formatError(error: unknown) {
    if (!error) {
      return '';
    }

    const graphQLError = (error as { graphQLErrors?: Array<{ message?: string }> })
      ?.graphQLErrors?.[0]?.message;

    if (graphQLError) {
      return graphQLError;
    }

    if (error instanceof Error) {
      return error.message;
    }

    return String(error);
  }

  render() {
    const { initialStudent, createEditStudent, onSuccess } = this.props;
    const { error, isDone } = this.state;

    return (
      <Formik<CreateStudentVariables['student']>
        initialValues={{
          ...defaultValues,
          ...initialStudent
        }}
        validateOnChange={false}
        validateOnBlur={true}
        onSubmit={async (student, { setSubmitting }) => {
          this.setState({ error: '' });

          try {
            await createEditStudent({ student: formatStudentInput(student) });
          } catch (submitError) {
            this.setState({ error: this.formatError(submitError) });
            setSubmitting(false);
            return;
          }

          this.setState({ isDone: true });
          setSubmitting(false);

          if (onSuccess) {
            window.setTimeout(() => onSuccess(), 1000);
          }
        }}
        validationSchema={createStudentSchema}
      >
        {({ isSubmitting, isValid, handleSubmit }) => {
          if (isSubmitting) {
            return <Loading />;
          }

          if (isDone) {
            return (
              <div style={{ textAlign: 'center', marginTop: 20 }}>
                <FontAwesome name='check' />
                <br />
                <br />
                Done!
              </div>
            );
          }

          return (
            <form onSubmit={handleSubmit}>
              <List>
                {error ? (
                  <Subheader style={styles.headerNote}>
                    {error}
                  </Subheader>
                ) : null}
                <Subheader style={styles.header}>User Information</Subheader>
                <ListItem innerDivStyle={styles.miniListItem}>
                  <Field
                    name='username'
                    floatingLabelText='Username'
                    component={FormikMaterialTextField}
                    fullWidth={true}
                  />
                </ListItem>
                <ListItem innerDivStyle={styles.miniListItem}>
                  <Field
                    name='email'
                    floatingLabelText='Email'
                    component={FormikMaterialTextField}
                    fullWidth={true}
                  />
                </ListItem>
                <ListItem innerDivStyle={styles.miniListItem}>
                  <Field
                    name='phoneNumber'
                    floatingLabelText='Phone Number (with country code)'
                    component={FormikMaterialTextField}
                    fullWidth={true}
                  />
                </ListItem>
                <ListItem innerDivStyle={styles.miniListItem}>
                  <Field
                    name='firstName'
                    floatingLabelText='First Name'
                    component={FormikMaterialTextField}
                    fullWidth={true}
                  />
                </ListItem>
                <ListItem innerDivStyle={styles.miniListItem}>
                  <Field
                    name='lastName'
                    floatingLabelText='Last Name'
                    component={FormikMaterialTextField}
                    fullWidth={true}
                  />
                </ListItem>
                <ListItem>
                  <RaisedButton
                    type='submit'
                    disabled={!isValid}
                    primary={true}
                    label='SAVE'
                    fullWidth={true}
                  />
                </ListItem>
              </List>
            </form>
          );
        }}
      </Formik>
    );
  }
}
